import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calculator, Save, AlertTriangle, CheckCircle2, ExternalLink } from "lucide-react";
import { PageHeader, Card, CopperButton, SlateButton, Modal, Field, NumberInput, Pill } from "@/components/common/UI";
import { FORMULAS } from "@/lib/formulas";
import api from "@/lib/api";
import { toast } from "sonner";

export default function FormulaRegistry() {
    const navigate = useNavigate();
    const [overrides, setOverrides] = useState({});
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // formula being edited
    const [newValue, setNewValue] = useState("");
    const [savedBanner, setSavedBanner] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/formulas");
            setOverrides(r.data || {});
        } catch (e) {
            // first run: collection may not exist yet — show defaults
            setOverrides({});
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const openEdit = (f) => {
        if (!f.editable) {
            toast.info("This formula is driven from Customers & Norms or Process Rates. Edit it there.");
            return;
        }
        setEditing(f);
        setNewValue(overrides[f.id] != null ? String(overrides[f.id]) : String(f.current_value));
    };

    const saveEdit = async () => {
        if (!editing) return;
        const v = parseFloat(newValue);
        if (Number.isNaN(v)) { toast.error("Enter a valid number"); return; }
        try {
            await api.put(`/formulas/${editing.id}`, { value: v });
            toast.success(`${editing.field} updated to ${v}`);
            setOverrides((o) => ({ ...o, [editing.id]: v }));
            setSavedBanner({ id: editing.id, field: editing.field, value: v, impacts: editing.impact_areas });
            setEditing(null);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to save");
        }
    };

    return (
        <div data-testid="formula-registry-page">
            <PageHeader
                title="Formula Registry"
                subtitle="Every cost formula used across CostIQ: view, audit and edit (where permitted). Changes show their downstream impact before save."
            />

            {savedBanner && (
                <div className="mt-4 rounded-md border border-amber-200 bg-amber-50/70 p-3.5" data-testid="impact-banner">
                    <div className="flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                        <div className="text-[12.5px] text-amber-900">
                            <div className="font-semibold mb-1">
                                Saved: <span className="font-mono">{savedBanner.field}</span> set to <span className="font-mono">{savedBanner.value}</span>
                            </div>
                            <div>This change will affect:</div>
                            <ul className="list-disc pl-5 mt-1">
                                {(savedBanner.impacts || []).map((i) => <li key={i}>{i}</li>)}
                            </ul>
                            <div className="mt-2 text-amber-700">
                                Applied live to the cost engine — any new ZBC or RFQ created from now on uses the new value. Existing records keep their snapshotted figures.
                            </div>
                            <button className="mt-2 underline text-amber-800" onClick={() => setSavedBanner(null)}>Dismiss</button>
                        </div>
                    </div>
                </div>
            )}

            <Card className="mt-5 p-0 overflow-hidden">
                <div className="rounded-md">
                    <table className="w-full text-[12.5px]">
                        <thead>
                            <tr className="bg-slate-50/60 border-b border-slate-200">
                                <th className="text-left py-2.5 px-4 eyebrow text-slate-500">Section</th>
                                <th className="text-left py-2.5 px-4 eyebrow text-slate-500">Field</th>
                                <th className="text-left py-2.5 px-4 eyebrow text-slate-500">Formula</th>
                                <th className="text-right py-2.5 px-4 eyebrow text-slate-500 w-28">Value</th>
                                <th className="text-left py-2.5 px-4 eyebrow text-slate-500 w-32">Impacts</th>
                                <th className="text-right py-2.5 px-4 w-52"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr><td colSpan={6} className="py-6 text-center text-slate-400">Loading…</td></tr>
                            )}
                            {!loading && FORMULAS.map((f) => {
                                const override = overrides[f.id];
                                const isOverridden = override != null && override !== f.current_value;
                                return (
                                    <tr key={f.id} className="border-b border-slate-100 hover:bg-slate-50/40" data-testid={`formula-row-${f.id}`}>
                                        <td className="py-2.5 px-4 text-slate-500">{f.section}</td>
                                        <td className="py-2.5 px-4 font-medium text-slate-800 flex items-center gap-1.5">
                                            <Calculator className="w-3.5 h-3.5 text-slate-400" />
                                            {f.field}
                                        </td>
                                        <td className="py-2.5 px-4">
                                            <code className="text-[11px] text-slate-600 whitespace-pre-wrap block max-w-md">{f.expression}</code>
                                            {f.notes && <div className="text-[10.5px] text-slate-400 mt-1">{f.notes}</div>}
                                        </td>
                                        <td className="py-2.5 px-4 text-right font-mono tabular-nums">
                                            {f.value_label ? (
                                                <>
                                                    <span className={isOverridden ? "text-[hsl(var(--copper))] font-semibold" : "text-slate-800"}>
                                                        {override != null ? override : f.current_value}
                                                    </span>
                                                    {isOverridden && <Pill tone="amber" className="ml-1.5 text-[9px]">OVERRIDE</Pill>}
                                                </>
                                            ) : (
                                                <span className="text-slate-300">—</span>
                                            )}
                                        </td>
                                        <td className="py-2.5 px-4 text-[11px] text-slate-500">
                                            {(f.impact_areas || []).slice(0, 2).join(" · ")}
                                            {f.impact_areas?.length > 2 && <span className="text-slate-400">+{f.impact_areas.length - 2}</span>}
                                        </td>
                                        <td className="py-2.5 px-4 text-right">
                                            {f.editable ? (
                                                <SlateButton onClick={() => openEdit(f)} className="h-7 px-2.5 text-xs" data-testid={`formula-edit-${f.id}`}>
                                                    Edit
                                                </SlateButton>
                                            ) : f.source_screen ? (
                                                <button
                                                    onClick={() => navigate(f.source_screen.path)}
                                                    className="inline-flex items-center gap-1 h-7 px-2.5 text-[11px] rounded-md border border-slate-200 bg-white hover:bg-slate-50 hover:border-[hsl(var(--copper))] hover:text-[hsl(var(--copper))] transition-colors text-slate-600 whitespace-nowrap"
                                                    data-testid={`formula-goto-${f.id}`}
                                                    title={`Edit on ${f.source_screen.label}`}
                                                >
                                                    Edit on {f.source_screen.label}
                                                    <ExternalLink className="w-3 h-3 shrink-0" />
                                                </button>
                                            ) : (
                                                <span className="text-[10.5px] text-slate-400" title="Composite formula, derived from other rows">Derived</span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </Card>

            <div className="mt-3 text-[11.5px] text-slate-500">
                <CheckCircle2 className="inline w-3.5 h-3.5 -mt-0.5 mr-1 text-emerald-500" />
                Every formula here is editable. <strong>Engine constants</strong> (Scrap Recoverable, ICC %, Rejection %, Tool Maint %, Profit + OH %) are edited inline above. <strong>Per-customer norms</strong> (Volvo ICC / Overhead / Profit / Discount / Volume Rebate) and <strong>process rates</strong> (Cutting / Welding by bracket) open their canonical edit screens — click the "Edit on …" link on the right. <strong>Derived</strong> rows (Should-Cost, Grand Total, Final Price) are computed from the rows above them and have nothing to tune directly.
            </div>

            <Modal open={!!editing} onClose={() => setEditing(null)} title={editing ? `Edit · ${editing.field}` : ""}>
                {editing && (
                    <div className="space-y-4">
                        <div className="rounded-md bg-slate-50 p-3 text-[12px]">
                            <div className="eyebrow text-slate-500 mb-1">FORMULA</div>
                            <pre className="font-mono text-[11.5px] text-slate-700 whitespace-pre-wrap">{editing.expression}</pre>
                        </div>
                        <Field label={`New value for ${editing.value_label || editing.field}`} required>
                            <NumberInput value={newValue} onChange={(e) => setNewValue(e.target.value)} step="0.01"
                                data-testid="formula-edit-value-input" />
                        </Field>
                        <div className="rounded-md border border-amber-200 bg-amber-50/60 p-3 text-[11.5px] text-amber-900">
                            <strong>Heads-up:</strong> after saving, the following will be affected:
                            <ul className="list-disc pl-5 mt-1">
                                {(editing.impact_areas || []).map((i) => <li key={i}>{i}</li>)}
                            </ul>
                            <div className="mt-1.5 text-amber-700">Existing records keep their snapshotted values — only new ZBCs / RFQs created after this change will pick it up.</div>
                        </div>
                        <div className="flex justify-end gap-2 pt-1">
                            <SlateButton onClick={() => setEditing(null)}>Cancel</SlateButton>
                            <CopperButton onClick={saveEdit} data-testid="formula-edit-save-btn">
                                <Save className="w-3.5 h-3.5" /> Save Override
                            </CopperButton>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
}
